import type { CinematicOverrides } from '../../../renderer/src/cinematic-sequence';
import { interpolateDepthOfField } from '../../../renderer/src/depth-of-field';
import { interpolatePresentationAppearance } from '../../../renderer/src/presentation-appearance';
import type { CinematicSettings } from './cinematic-plan';
import type { CameraPlan } from './camera-plan';
import { blendCameraPoses } from './camera-pose-blend';
import { cinematicTransitionAmount } from './cinematic-appearance';
import { cinematicPropertyGroups } from './cinematic-property-timeline';
import type { TimedCinematicWaypoint } from './cinematic-timeline';

/** Concurrent sparse keys affect only their own properties, including during transitions. */
export function compilePropertyPlan(timeline: readonly TimedCinematicWaypoint[], duration: number, initial: CinematicSettings,
  cameraFor: (settings: CinematicSettings) => CameraPlan) {
  const groups = cinematicPropertyGroups(timeline, duration);
  return (progress: number) => {
    const seconds = progress * duration;
    let settings = { ...initial }, depthOfField = initial.depthOfField, presentationAppearance = initial.presentationAppearance ?? {};
    let variants = [{ settings: initial, weight: 1 }];
    const transitions: Partial<Record<keyof CinematicOverrides, { id: string; from: CinematicSettings; to: CinematicSettings; amount: number }>> = {};
    for (const item of groups) {
      if (seconds + 1e-9 < item.transitionStartSeconds) continue;
      const overrides = item.waypoint.overrides, amount = cinematicTransitionAmount(item, seconds), from = settings;
      const to: CinematicSettings = { ...settings,
        cameraStyle: overrides.cameraStyle ?? settings.cameraStyle, cameraDistance: overrides.cameraDistance ?? settings.cameraDistance,
        cameraGimbal: overrides.cameraGimbal ?? settings.cameraGimbal,
        mapStyle: seconds + 1e-9 >= item.seconds ? overrides.mapStyle ?? settings.mapStyle : settings.mapStyle,
        cameraZoom: overrides.cameraZoom === undefined ? settings.cameraZoom : overrides.cameraZoom,
        cameraAngle: overrides.cameraAngle === undefined ? settings.cameraAngle : overrides.cameraAngle,
        depthOfField: overrides.depthOfField ?? settings.depthOfField,
        presentationAppearance: { ...settings.presentationAppearance, ...overrides.presentationAppearance } };
      settings = to;
      for (const property of Object.keys(overrides) as (keyof CinematicOverrides)[]) {
        if (property === 'mapStyle' && seconds + 1e-9 < item.seconds) continue;
        transitions[property] = { id: item.waypoint.id, from, to, amount: property === 'mapStyle' ? 1 : amount };
      }
      if (overrides.depthOfField !== undefined) depthOfField = interpolateDepthOfField(depthOfField, overrides.depthOfField, amount);
      if (overrides.presentationAppearance !== undefined) presentationAppearance = interpolatePresentationAppearance(presentationAppearance,
        { ...presentationAppearance, ...overrides.presentationAppearance }, amount);
      const cameraOverrides = Object.fromEntries(Object.entries(overrides).filter(([property]) =>
        ['cameraStyle', 'cameraDistance', 'cameraAngle', 'cameraZoom'].includes(property)));
      if (!Object.keys(cameraOverrides).length || amount === 0) continue;
      variants = variants.flatMap(variant => {
        const destination = { settings: { ...variant.settings, ...cameraOverrides }, weight: variant.weight * amount };
        return amount === 1 ? [destination] : [{ ...variant, weight: variant.weight * (1 - amount) }, destination];
      });
    }
    let camera = cameraFor(variants[0]!.settings).evaluate(progress), weight = variants[0]!.weight;
    for (const variant of variants.slice(1)) {
      camera = blendCameraPoses(camera, cameraFor(variant.settings).evaluate(progress), variant.weight / (weight + variant.weight));
      weight += variant.weight;
    }
    const angle = transitions.cameraAngle;
    return { camera, depthOfField, transitions, settings: { ...settings, depthOfField, presentationAppearance,
      cameraAngle: angle && angle.amount < 1 && (angle.from.cameraAngle !== null || angle.to.cameraAngle !== null) ? camera.pitch : settings.cameraAngle } };
  };
}
